/** localStorage key holding the current session UUID. */
export const SESSION_ID_KEY = 'backstage_session_id';

/** localStorage key holding the last explicit activity time, epoch ms as a decimal string (String(Date.now())). */
export const SESSION_LAST_ACTIVE_KEY = 'backstage_session_last_active';

/** Inactivity window. A session is expired when now - lastActive > SESSION_TIMEOUT_MS (strictly greater). */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

/** Minimum interval between two activity writes from DOM listeners (click/keydown). */
export const ACTIVITY_THROTTLE_MS = 5 * 1000;

type SessionState = { id?: string; lastActive?: number };
type ActiveSession = { id: string; lastActive: number };

// Once localStorage throws, stay in memory until the page reloads. If only
// writes failed while reads kept working, every call would see the same stale
// expired value and rotate on every event.
let storageBroken = false;
let memoryState: SessionState = {};

function readState(): SessionState {
  if (!storageBroken) {
    try {
      const id = window.localStorage.getItem(SESSION_ID_KEY);
      const raw = window.localStorage.getItem(SESSION_LAST_ACTIVE_KEY);
      const lastActive = raw === null || raw.trim() === '' ? NaN : Number(raw);
      if (id && Number.isFinite(lastActive)) {
        return { id, lastActive };
      }
      return {};
    } catch (_e) {
      storageBroken = true;
    }
  }
  return { ...memoryState };
}

function writeState(id: string, lastActive: number): void {
  if (!storageBroken) {
    try {
      window.localStorage.setItem(SESSION_ID_KEY, id);
      window.localStorage.setItem(SESSION_LAST_ACTIVE_KEY, String(lastActive));
      return;
    } catch (_e) {
      storageBroken = true;
    }
  }
  memoryState = { id, lastActive };
}

// Extends an active session by writing only the timestamp. Never write the id
// here: another tab may have rotated the session since this tab read it, and
// writing the stale id back would resurrect the old session. `id` is only used
// for the in-memory fallback.
function writeLastActive(id: string, lastActive: number): void {
  if (!storageBroken) {
    try {
      window.localStorage.setItem(SESSION_LAST_ACTIVE_KEY, String(lastActive));
      return;
    } catch (_e) {
      storageBroken = true;
    }
  }
  memoryState = { id, lastActive };
}

// A lastActive in the future (clock moved back) counts as active.
function isActive(state: SessionState, now: number): state is ActiveSession {
  return (
    state.id !== undefined &&
    state.lastActive !== undefined &&
    now - state.lastActive <= SESSION_TIMEOUT_MS
  );
}

function formatUuidV4(source: ArrayLike<number>): string {
  const bytes = Array.from(source);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function generateId(): string {
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
    if (c && typeof c.getRandomValues === 'function') {
      return formatUuidV4(c.getRandomValues(new Uint8Array(16)));
    }
  } catch (_e) {
    // fall through to Math.random
  }
  // session_id is a grouping key, not a secret or a security identifier, so a
  // non-cryptographic generator is acceptable when crypto is unavailable.
  return formatUuidV4(
    Array.from({ length: 16 }, () => Math.floor(Math.random() * 256)),
  );
}

/**
 * Shared by getSessionId and touchActivity. localStorage has no atomic
 * compare-and-set and the Web Locks API is async, so it cannot sit inside a
 * synchronous hook. Re-reading before the write and reading back after it
 * narrows the cross-tab race to a few synchronous operations without
 * eliminating it; that is a deliberate trade-off.
 */
function rotate(now: number, extend: boolean): string {
  const candidate = generateId();

  const current = readState();
  if (isActive(current, now)) {
    if (extend) {
      writeLastActive(current.id, now);
      return readState().id ?? current.id;
    }
    return current.id;
  }

  writeState(candidate, now);

  const stored = readState().id;
  return stored && stored !== candidate ? stored : candidate;
}

/**
 * Session ID to stamp on an event being emitted *now*.
 * Returns the active session ID; if there is none or it has expired, rotates
 * and returns the new ID. Does NOT extend an active session (does not write
 * last_active) — sending an event is not activity.
 */
export function getSessionId(now?: number): string {
  try {
    const at = now ?? Date.now();
    const state = readState();
    return isActive(state, at) ? state.id : rotate(at, false);
  } catch (_e) {
    return generateId();
  }
}

/**
 * Read-only: the current session ID if it exists and has NOT expired, else
 * undefined. Never rotates, never writes to storage.
 */
export function peekActiveSessionId(now?: number): string | undefined {
  try {
    const state = readState();
    return isActive(state, now ?? Date.now()) ? state.id : undefined;
  } catch (_e) {
    return undefined;
  }
}

/**
 * Explicit user activity. Rotates if there is no session or it has expired,
 * otherwise writes last_active = now. Returns the resulting (active) session ID.
 */
export function touchActivity(now?: number): string {
  try {
    const at = now ?? Date.now();
    const state = readState();
    if (isActive(state, at)) {
      writeLastActive(state.id, at);
      // Another tab may have rotated between the read and the write; report
      // the session that is stored now, not the one this tab read.
      return readState().id ?? state.id;
    }
    return rotate(at, true);
  } catch (_e) {
    return generateId();
  }
}

/**
 * Installs throttled `click` and `keydown` listeners on `target`
 * (capture: true, passive: true) that call touchActivity() at most once per
 * ACTIVITY_THROTTLE_MS (leading edge). Returns a function that removes both
 * listeners. If `target` is undefined (no document), it is a no-op.
 *
 * Capture is needed so a component calling stopPropagation does not hide the
 * activity.
 */
export function startActivityTracking(
  target?: Pick<Document, 'addEventListener' | 'removeEventListener'>,
): () => void {
  if (!target) {
    return () => {};
  }

  let lastRun: number | undefined;
  const handler = () => {
    try {
      const now = Date.now();
      if (lastRun !== undefined) {
        const elapsed = now - lastRun;
        if (elapsed >= 0 && elapsed < ACTIVITY_THROTTLE_MS) {
          return;
        }
      }
      lastRun = now;
      touchActivity(now);
    } catch (_e) {
      // never crash the portal
    }
  };

  target.addEventListener('click', handler, { capture: true, passive: true });
  target.addEventListener('keydown', handler, { capture: true, passive: true });

  return () => {
    target.removeEventListener('click', handler, { capture: true });
    target.removeEventListener('keydown', handler, { capture: true });
  };
}

/**
 * mixpanel-browser `hooks.before_track` implementation. Returns
 * [eventName, properties] with `session_id` added when it is missing.
 *
 * Must always return an array: the SDK treats `undefined` as a failure and
 * drops the event.
 */
export function beforeTrackHook(
  eventName: string,
  properties?: Record<string, any>,
): [string, Record<string, any>] {
  try {
    const existing = properties?.session_id;
    if (
      properties &&
      Object.prototype.hasOwnProperty.call(properties, 'session_id') &&
      typeof existing === 'string' &&
      existing !== ''
    ) {
      return [eventName, properties];
    }
    return [eventName, { ...(properties ?? {}), session_id: getSessionId() }];
  } catch (_e) {
    return [eventName, properties ?? {}];
  }
}

/** Test-only: clear in-memory fallback state and the "storage broken" flag. Does not touch localStorage. */
export function __resetSessionForTest(): void {
  storageBroken = false;
  memoryState = {};
}
